/*
 * Copyright (c) Nexign, JSC, 2021.
 */
/**
 * Campaign Info modal remove
 *
 * @author Yakov Gushchin
 * @version 1.13.0
 * @since 1.12.0
 */

import { useCallback } from 'react';

import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';

import { Button, LinkBlock } from '../../../components/common';
import {
    makeSelectPageCampaignInfoTranslation,
    makeSelectTranslationCommon,
} from '../../../modules/internationalization/intSelectors';
import { ICommonTranslation } from '../../../modules/internationalization/types';
import { IPageCampaignInfoTranslation } from '../../../modules/internationalization/types/pages/campaignInfo';
import Modal from '../../../modules/modal/components/Modal';
import { EModalStatus, IModalRenderProps } from '../../../modules/modal/types/modalTypes';
import ButtonServiceUserDenied from '../../../modules/role/components/ButtonServiceUserDenied';
import { IRootReducer } from '../../../reducers/rootReducer';
import { campaignInfoRemoveAction } from '../campaignInfoActions';
import { ICampaignInfoGeneral } from '../types/campaignInfoTypes';

type TCampaignInfoModalRemove = Pick<IModalRenderProps, 'onCloseModal'> & Pick<ICampaignInfoGeneral, 'type'>
    & { isInformCampaignType: boolean };

const CampaignInfoModalRemove = (props: TCampaignInfoModalRemove & IConnectedProps & IDispatchProps) => {

    const {
        isInformCampaignType,
        type,
        translationCampaignInfo,
        translationCommon,

        onCloseModal,
        remove,
    } = props;

    const getTranslationStart = useCallback(() => isInformCampaignType
            ? translationCampaignInfo.common.modalRemove[EModalStatus.start]
            : translationCampaignInfo[type].modalRemove[EModalStatus.start],
        [isInformCampaignType, translationCampaignInfo, type]);

    const getTranslationFailure = useCallback(() =>
            translationCampaignInfo.common.modalRemove[EModalStatus.failure],
        [translationCampaignInfo]);

    return (
        <Modal dataName="campaign-info-remove-modal" width="380px">

            <Modal.Status status={EModalStatus.start}>
                <Modal.Title>{getTranslationStart().title}</Modal.Title>
                <Modal.Text>{getTranslationStart().text}</Modal.Text>
                <Modal.Control>
                    <ButtonServiceUserDenied dataName="campaign-info-remove-modal-btn-remove" onClick={remove}>
                        {translationCommon.modal.remove}
                    </ButtonServiceUserDenied>
                    <LinkBlock dataName="campaign-info-remove-modal-btn-cancel" onClick={onCloseModal}>
                        {translationCommon.modal.close}
                    </LinkBlock>
                </Modal.Control>
            </Modal.Status>

            <Modal.Status status={EModalStatus.failure}>
                <Modal.Title>{getTranslationFailure().title}</Modal.Title>
                <Modal.Text>{getTranslationFailure().text}</Modal.Text>
                <Modal.Control>
                    <Button dataName="campaign-info-remove-modal-btn-cancel" onClick={onCloseModal}>
                        {translationCommon.modal.close}
                    </Button>
                </Modal.Control>
            </Modal.Status>
        </Modal>
    );
};

export interface IConnectedProps {
    translationCampaignInfo: IPageCampaignInfoTranslation;
    translationCommon: ICommonTranslation;
}

export const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
    translationCampaignInfo: makeSelectPageCampaignInfoTranslation(),
    translationCommon: makeSelectTranslationCommon(),
});

export interface IDispatchProps {
    remove: () => void;
}

export const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
    remove: () => dispatch(campaignInfoRemoveAction()),
});

export default connect(mapStateToProps, mapDispatchToProps)(CampaignInfoModalRemove);
